/**
 * Connection lifecycle client — create, consent, test, fix, and delete the
 * connections that flows (and connector operations) authenticate through.
 *
 * Plane and posture, stated honestly:
 * - Endpoints are the Power Platform API `connectivity` namespace on the
 *   global host, environment-scoped in the PATH and additionally filtered by
 *   environment in the QUERY:
 *   `https://api.powerplatform.com/connectivity/environments/{envId}/...
 *    ?api-version=1&$filter=environment eq '{envId}'`
 *   Both are required — the nested connector route answers
 *   `MissingEnvironmentFilter` without the query filter. Authenticated with
 *   the same `https://api.powerplatform.com` token used for Power Pages.
 * - Consent uses Microsoft's FlowAgent per-environment route. Default
 *   environments retain `default` before the GUID-derived hostname label;
 *   dropping it caused the historical NXDOMAIN/route-unavailable diagnosis.
 *   Other connection routes remain on the previously verified global host.
 * - Microsoft formally documents this exact PUT shape for one connector
 *   (`shared_uiflow`, the desktop-flows ALM doc) and ships the generalized
 *   form (any connector + getConsentLink) in its own MIT-licensed FlowAgent
 *   plugin. There is no published REST reference for general connectors —
 *   the documented alternatives (Power Automate Management connector, pac,
 *   admin PowerShell) are not callable from a local MCP server. This is the
 *   same supportedness class as the maker plane the rest of this server has
 *   always used for flows and runs.
 * - Verified live against a real tenant before release (see CHANGELOG).
 */

import { randomUUID } from "node:crypto";
import { requestWithRetry, readResponseBody } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
  ValidationError,
  wrapApiCall,
} from "../utils/errors.js";
import { sanitizeErrorMessage, validateEnvironmentId } from "../utils/security.js";
import { RESOURCES } from "../auth/types.js";
import { logger } from "../utils/logger.js";

const CONNECTIVITY_API_VERSION = "1";
/** Where Microsoft's consent flow bounces back to when consent completes. */
const CONSENT_REDIRECT_URL = "https://make.powerautomate.com";
const CONSENT_TIMEOUT_MS = 30_000;
const CONSENT_MAX_RESPONSE_BYTES = 64 * 1024;
const CONSENT_MAX_URL_CHARS = 16 * 1024;

/**
 * Deep link to a connection in the maker portal — the documented way to reach
 * a connection (the same `apiName`/`connectionName` query pair the portal uses
 * in its own URLs). This is the fallback when the service exposes no
 * consent-link route for the environment.
 */
export function makerPortalConnectionUrl(
  environmentId: string,
  connector: string,
  connectionName: string,
): string {
  const query = new URLSearchParams({ apiName: connector, connectionName });
  return `https://make.powerautomate.com/manage/environments/${environmentId}/connections?${query.toString()}`;
}

export interface ConnectionsApiConfig {
  authProvider: AuthProvider;
  defaultEnvironmentId: string;
}

export interface ConnectivityConnectionStatus {
  status?: string;
  target?: string;
  error?: { code?: string; message?: string };
  [key: string]: unknown;
}

export interface ConnectivityConnection {
  name?: string;
  id?: string;
  properties?: {
    displayName?: string;
    statuses?: ConnectivityConnectionStatus[];
    createdBy?: { displayName?: string; userPrincipalName?: string };
    createdTime?: string;
    lastModifiedTime?: string;
    apiId?: string;
    testLinks?: Array<{ requestUri?: string; method?: string }>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ConsentLinkResult {
  /** Browser URL the user opens to grant OAuth consent, when required. */
  consentLink?: string;
  /** First-party silent-auth URI (M365 connectors), when offered. */
  firstPartyLoginUri?: string;
  [key: string]: unknown;
}

/** A 4xx that can carry the "no such member" deserialization refusal. */
function rejectsParameterSets(statusCode: number): boolean {
  return statusCode === 400 || statusCode === 422;
}

/** Only an explicit RouteNotFound response warrants a portal fallback. */
export class ConsentLinkUnavailableError extends Error {
  constructor() {
    super("This environment exposes no consent-link route");
    this.name = "ConsentLinkUnavailableError";
  }
}

/** One of a connector's published authentication modes. */
export interface ConnectorParameterSet {
  name: string;
  displayName?: string;
  /** Parameter names the mode expects (vaultName, clientId, …). */
  parameters: string[];
}

/** Render auth modes for an error the caller can act on without another lookup. */
function describeParameterSets(sets: ConnectorParameterSet[]): string {
  return sets
    .map((set) => (set.displayName ? `${set.name} (${set.displayName})` : set.name))
    .join(", ");
}

export interface CreateConnectionResult {
  connectionName: string;
  /** The environment the connection was created in (resolved, never blank). */
  environmentId: string;
  connection: ConnectivityConnection;
  connected: boolean;
  consent?: ConsentLinkResult;
}

/** Connector API names look like shared_sharepointonline / shared_office365. */
function validateConnectorName(value: string): string {
  const trimmed = value.trim();
  if (!/^[a-z0-9][a-z0-9_-]{1,80}$/i.test(trimmed)) {
    throw new ValidationError(
      `Connector name "${value}" is not a valid connector API name (expected something like shared_sharepointonline)`,
    );
  }
  return trimmed;
}

function validateConnectionName(value: string): string {
  const trimmed = value.trim();
  if (!/^[a-z0-9][a-z0-9-]{1,80}$/i.test(trimmed)) {
    throw new ValidationError(`Connection name "${value}" contains unsupported characters`);
  }
  return trimmed;
}

export function isConnected(connection: ConnectivityConnection): boolean {
  return (connection.properties?.statuses ?? []).some((s) => s.status === "Connected");
}

/** Global Power Platform API host that serves the connectivity routes. */
export const CONNECTIVITY_HOST = "api.powerplatform.com";

/**
 * Per-environment API hostname (ID with separators removed, split 30/2 with a
 * dot), retaining the `default` prefix exactly as Microsoft's FlowAgent
 * ppapiBaseUrl does. No configurable host or authority is accepted here.
 */
export function environmentIdToApiHost(environmentId: string): string {
  if (!/^(?:Default-)?[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(environmentId)) {
    throw new ValidationError(
      "Environment ID must be a full GUID, optionally prefixed with Default-, to derive its Power Platform API hostname",
    );
  }
  const isDefault = /^Default-/i.test(environmentId);
  const raw = environmentId
    .replace(/^Default-/i, "")
    .replace(/-/g, "")
    .toLowerCase();
  return `${isDefault ? "default" : ""}${raw.slice(0, 30)}.${raw.slice(30, 32)}.environment.api.powerplatform.com`;
}

function consentRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** Validate, but never rewrite or follow, the service's user sign-in URL. */
function validatedConsentLink(value: unknown): string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > CONSENT_MAX_URL_CHARS ||
    /[\s\\\p{Cc}]/u.test(value) ||
    !/^https:\/\//i.test(value)
  ) {
    throw new ApiError(
      "Get consent link: no valid HTTPS user sign-in link was returned; the existing connection was not replaced or reauthorized",
      502,
    );
  }
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || !url.hostname || url.username || url.password)
      throw new Error();
  } catch {
    throw new ApiError(
      "Get consent link: no valid HTTPS user sign-in link was returned; the existing connection was not replaced or reauthorized",
      502,
    );
  }
  return value;
}

/** Client for the environment-scoped connectivity (connections) surface. */
export class ConnectionsApi {
  private readonly authProvider: AuthProvider;
  private readonly defaultEnvironmentId: string;

  constructor(config: ConnectionsApiConfig) {
    this.authProvider = config.authProvider;
    this.defaultEnvironmentId = config.defaultEnvironmentId;
  }

  /** Resolve an optional environment argument against the configured default. */
  resolveEnvironmentId(environmentId?: string): string {
    return environmentId ?? this.defaultEnvironmentId;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM_API);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  /**
   * Both the path segment and the `$filter` are required: the nested connector
   * routes reject a request without the environment filter
   * (`MissingEnvironmentFilter`), verified live.
   */
  private buildUrl(environmentId: string, path: string): string {
    const envId = validateEnvironmentId(environmentId);
    // Percent-encoded (not URLSearchParams' "+" for spaces) to match the exact
    // form verified against the live service.
    const filter = encodeURIComponent(`environment eq '${envId}'`);
    return `https://${CONNECTIVITY_HOST}/connectivity/environments/${envId}${path}?api-version=${CONNECTIVITY_API_VERSION}&$filter=${filter}`;
  }

  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string,
  ): Promise<T> {
    if (response.statusCode === 429) {
      const retryAfter = response.headers["retry-after"];
      await response.body.text().catch(() => undefined);
      const seconds = retryAfter
        ? Number(Array.isArray(retryAfter) ? retryAfter[0] : retryAfter)
        : NaN;
      throw new RateLimitError(Number.isFinite(seconds) ? seconds * 1000 : undefined);
    }
    const { statusCode, text } = await readResponseBody(response, context);
    if (statusCode === 401) {
      throw new AuthenticationError(
        `${context}: Power Platform API authentication failed. Connection management needs the "Power Platform API" delegated permission on the app registration WITH admin consent — re-running --setup does not grant it (see the App Registration table in the README).`,
      );
    }
    if (statusCode === 403) {
      throw new AuthorizationError(
        `${context}: access denied. Managing connections requires maker access to the environment (Environment Maker role) and the Power Platform API permission granted at setup.`,
      );
    }
    if (statusCode >= 400) {
      let details: string;
      try {
        details = JSON.stringify(JSON.parse(text));
      } catch {
        details = text;
      }
      throw new ApiError(`${context}: connectivity API error`, statusCode, sanitizeErrorMessage(details));
    }
    if (!text) return {} as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError(`${context}: connectivity API returned a non-JSON response`, statusCode);
    }
  }

  /** List connections in the environment, optionally for one connector. */
  async listConnections(options: { connector?: string; environmentId?: string } = {}): Promise<
    ConnectivityConnection[]
  > {
    const envId = options.environmentId ?? this.defaultEnvironmentId;
    const context = "List connections";
    const headers = await this.getAuthHeaders();
    const all = await wrapApiCall(async () => {
      const response = await requestWithRetry(this.buildUrl(envId, "/connections"), {
        method: "GET",
        headers,
      });
      const data = await this.handleResponse<{ value: ConnectivityConnection[] }>(response, context);
      return data.value ?? [];
    }, context);

    if (!options.connector) return all;
    const wanted = validateConnectorName(options.connector);
    // apiId looks like /providers/Microsoft.PowerApps/apis/shared_office365
    return all.filter((c) => String(c.properties?.apiId ?? "").split("/").pop() === wanted);
  }

  /** Get one connection (its statuses carry connected/error state). */
  async getConnection(
    connector: string,
    connectionName: string,
    environmentId?: string,
  ): Promise<ConnectivityConnection> {
    const envId = environmentId ?? this.defaultEnvironmentId;
    const safeConnector = validateConnectorName(connector);
    const safeName = validateConnectionName(connectionName);
    const context = "Get connection";
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(envId, `/connectors/${safeConnector}/connections/${safeName}`),
        { method: "GET", headers },
      );
      return this.handleResponse<ConnectivityConnection>(response, context);
    }, context);
  }

  /**
   * Create a connection. For OAuth connectors the result usually starts
   * unauthenticated — the returned consent link completes it in a browser.
   */
  async createConnection(options: {
    connector: string;
    environmentId?: string;
    displayName?: string;
    /** Connector-specific connection parameters (API-key connectors etc.). */
    parameters?: Record<string, unknown>;
    /**
     * Which authentication mode to create the connection under, for connectors
     * that publish several (`oauthDefault`, `oauthServicePrincipal`, …). See
     * {@link listConnectorParameterSets}.
     */
    parameterSet?: string;
  }): Promise<CreateConnectionResult> {
    const envId = options.environmentId ?? this.defaultEnvironmentId;
    const safeConnector = validateConnectorName(options.connector);
    const context = "Create connection";
    // Mint the connection ID client-side (the documented shared_uiflow shape).
    const connectionName = `${safeConnector.replace(/^shared_/, "").replace(/_/g, "-").slice(0, 20)}-${randomUUID().slice(0, 8)}`;

    const parameterSet = await this.resolveParameterSet(
      safeConnector,
      envId,
      options.parameterSet,
    );

    // A connector with several auth modes must be told which one to use. Sending
    // bare connectionParameters instead creates a connection with no auth mode:
    // the service accepts it, but it can never be signed in — the portal shows
    // the parameter form with no consent step, so "reconnect" loops forever.
    const authShape = parameterSet
      ? {
          connectionParameterSets: {
            name: parameterSet,
            values: Object.fromEntries(
              Object.entries(options.parameters ?? {}).map(([key, value]) => [key, { value }]),
            ),
          },
        }
      : { connectionParameters: options.parameters ?? {} };

    const body: Record<string, unknown> = {
      properties: {
        environment: {
          id: `/providers/Microsoft.PowerApps/environments/${envId}`,
          name: envId,
        },
        ...authShape,
        ...(options.displayName ? { displayName: options.displayName } : {}),
      },
    };

    const headers = await this.getAuthHeaders();
    const createUrl = this.buildUrl(
      envId,
      `/connectors/${safeConnector}/connections/${connectionName}`,
    );
    const connection = await wrapApiCall(async () => {
      const response = await requestWithRetry(createUrl, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });
      // Some connectors publish connectionParameterSets yet their create
      // contract has no such member — shared_microsoftcopilotstudio answers
      // "Could not find member 'connectionParameterSets' on object of type
      // 'ConnectionPropertiesDefinition'" (verified live). Only that exact
      // deserialization refusal is retried, and only with the plain shape the
      // connector also publishes; every other failure is reported unchanged so
      // a real error is never masked by a second attempt.
      if (parameterSet !== undefined && rejectsParameterSets(response.statusCode)) {
        const { text } = await readResponseBody(response, context).catch(() => ({ text: "" }));
        if (/connectionParameterSets/.test(text)) {
          const retry = await requestWithRetry(createUrl, {
            method: "PUT",
            headers,
            body: JSON.stringify({
              ...body,
              properties: {
                ...(body["properties"] as Record<string, unknown>),
                connectionParameterSets: undefined,
                connectionParameters: options.parameters ?? {},
              },
            }),
          });
          return this.handleResponse<ConnectivityConnection>(retry, context);
        }
        throw new ApiError(`${context}: ${text.slice(0, 400)}`, response.statusCode);
      }
      return this.handleResponse<ConnectivityConnection>(response, context);
    }, context);

    if (isConnected(connection)) {
      return { connectionName, environmentId: envId, connection, connected: true };
    }

    // Not connected yet — fetch the consent link so the user can finish auth.
    let consent: ConsentLinkResult | undefined;
    try {
      consent = await this.getConsentLink(safeConnector, connectionName, envId);
    } catch (error) {
      if (error instanceof ConsentLinkUnavailableError) {
        return { connectionName, environmentId: envId, connection, connected: false };
      }
      // Some connectors need no consent and simply take a moment to settle.
      logger.debug(
        { error: error instanceof Error ? error.message : String(error) },
        "Consent link fetch failed (connector may not require OAuth)",
      );
    }

    return { connectionName, environmentId: envId, connection, connected: false, consent };
  }

  /**
   * The authentication modes a connector publishes, or `undefined` when that
   * can't be established. Connectors with a single implicit mode report none.
   */
  async listConnectorParameterSets(
    connector: string,
    environmentId?: string,
  ): Promise<ConnectorParameterSet[] | undefined> {
    const envId = environmentId ?? this.defaultEnvironmentId;
    const safeConnector = validateConnectorName(connector);
    try {
      const headers = await this.getAuthHeaders();
      const response = await requestWithRetry(this.buildUrl(envId, `/connectors/${safeConnector}`), {
        method: "GET",
        headers,
      });
      if (response.statusCode < 200 || response.statusCode >= 300) {
        await response.body.text().catch(() => undefined);
        return undefined;
      }
      const { text } = await readResponseBody(response, "List connector authentication modes");
      const body = JSON.parse(text) as {
        properties?: { connectionParameterSets?: { values?: unknown[] } };
      };
      const values = body?.properties?.connectionParameterSets?.values;
      if (!Array.isArray(values)) return undefined;
      const sets: ConnectorParameterSet[] = [];
      for (const entry of values) {
        const value = entry as {
          name?: unknown;
          uiDefinition?: { displayName?: unknown };
          parameters?: Record<string, unknown>;
        };
        if (typeof value?.name !== "string") continue;
        const displayName = value.uiDefinition?.displayName;
        sets.push({
          name: value.name,
          ...(typeof displayName === "string" ? { displayName } : {}),
          parameters: Object.keys(value.parameters ?? {}),
        });
      }
      return sets;
    } catch {
      // Discovery is an enhancement, never a gate: if the connector can't be
      // read we fall back to the historical single-mode create rather than
      // refusing to create a connection that may well be fine.
      return undefined;
    }
  }

  /**
   * Decide which auth mode a create should use. Unknown metadata falls through
   * to the legacy shape; a genuine ambiguity is refused with the real choices,
   * because creating the wrong shape produces a connection nobody can sign in to.
   */
  private async resolveParameterSet(
    connector: string,
    environmentId: string,
    requested?: string,
  ): Promise<string | undefined> {
    const sets = await this.listConnectorParameterSets(connector, environmentId);
    if (requested) {
      if (sets && sets.length > 0 && !sets.some((set) => set.name === requested)) {
        throw new ValidationError(
          `Connector ${connector} has no authentication mode '${requested}'. Available: ${describeParameterSets(sets)}.`,
        );
      }
      return requested;
    }
    if (!sets || sets.length === 0) return undefined;
    if (sets.length === 1) return sets[0]?.name;
    throw new ValidationError(
      `Connector ${connector} supports several authentication modes, so one must be chosen — ` +
        `creating without it yields a connection that can never be signed in. ` +
        `Pass parameterSet as one of: ${describeParameterSets(sets)}.`,
    );
  }

  /**
   * Bounded, single-attempt consent transport. Never expose an upstream body,
   * transport error, first-party authentication URI, or response headers.
   */
  private async requestConsentJson(
    url: string,
    method: "GET" | "POST",
    headers: Record<string, string>,
    context: string,
  ): Promise<Record<string, unknown>> {
    let response: Awaited<ReturnType<typeof requestWithRetry>>;
    let text: string;
    try {
      response = await requestWithRetry(
        url,
        {
          method,
          headers,
          ...(method === "POST"
            ? { body: JSON.stringify({ redirectUrl: CONSENT_REDIRECT_URL }) }
            : {}),
          signal: AbortSignal.timeout(CONSENT_TIMEOUT_MS),
          headersTimeout: CONSENT_TIMEOUT_MS,
          bodyTimeout: CONSENT_TIMEOUT_MS,
        },
        { maxAttempts: 1 },
      );
      const chunks: Buffer[] = [];
      let size = 0;
      for await (const chunk of response.body as AsyncIterable<Uint8Array>) {
        size += chunk.length;
        if (size > CONSENT_MAX_RESPONSE_BYTES) {
          response.body.destroy();
          throw new ApiError(`${context}: response exceeds the 64 KiB safety limit`, 413);
        }
        chunks.push(Buffer.from(chunk));
      }
      text = Buffer.concat(chunks).toString("utf8");
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        `${context}: request failed or timed out; no automatic retry was attempted`,
        502,
      );
    }
    const status = response.statusCode;
    if (status === 401) {
      throw new AuthenticationError(
        `${context}: Power Platform API authentication was refused; no connection reauthorization was completed`,
      );
    }
    if (status === 403) {
      throw new AuthorizationError(
        `${context}: access to this connection was refused; existing connection permissions and environment access must be reviewed`,
      );
    }
    if (status === 429) {
      const raw = response.headers["retry-after"];
      const seconds = typeof raw === "string" && /^\d{1,6}$/.test(raw) ? Number(raw) : NaN;
      throw new RateLimitError(Number.isFinite(seconds) ? seconds * 1000 : undefined);
    }
    let result: Record<string, unknown> | undefined;
    try {
      result = consentRecord(JSON.parse(text));
    } catch {
      // Neither malformed response content nor OAuth data belongs in errors.
    }
    if (
      method === "POST" &&
      status === 404 &&
      (result?.code === "RouteNotFound" || consentRecord(result?.error)?.code === "RouteNotFound")
    ) {
      throw new ConsentLinkUnavailableError();
    }
    // undici.request does not follow redirects; reject every 3xx rather than
    // forwarding this authenticated request to a service-provided Location.
    if (status < 200 || status >= 300) {
      throw new ApiError(
        `${context}: connectivity API refused the request; no automatic retry was attempted`,
        status,
      );
    }
    const contentType = response.headers["content-type"];
    if (
      !result ||
      typeof contentType !== "string" ||
      !/^application\/(?:[a-z0-9!#$&^_.+-]+\+)?json(?:\s*;|$)/i.test(contentType)
    ) {
      throw new ApiError(`${context}: service returned an invalid JSON object response`, 502);
    }
    return result;
  }

  /** Fetch a user sign-in handoff for the verified existing connection. */
  async getConsentLink(
    connector: string,
    connectionName: string,
    environmentId?: string,
  ): Promise<ConsentLinkResult> {
    const envId = this.resolveEnvironmentId(environmentId);
    // Validate every target before acquiring a token. This operation's public
    // source contract accepts a full environment ID, not an arbitrary host.
    const host = environmentIdToApiHost(envId);
    const safeConnector = validateConnectorName(connector);
    const safeName = validateConnectionName(connectionName);
    const context = "Get consent link";
    const headers = await this.getAuthHeaders();
    const existing = await this.requestConsentJson(
      this.buildUrl(envId, `/connectors/${safeConnector}/connections/${safeName}`),
      "GET",
      headers,
      "Verify connection before consent",
    );
    const properties = consentRecord(existing.properties);
    const environment = consentRecord(properties?.environment);
    const apiId = `/providers/Microsoft.PowerApps/apis/${safeConnector}`;
    if (
      existing.name !== safeName ||
      existing.id !== `${apiId}/connections/${safeName}` ||
      properties?.apiId !== apiId ||
      environment?.name !== envId ||
      (environment.id !== undefined &&
        environment.id !== `/providers/Microsoft.PowerApps/environments/${envId}`)
    ) {
      throw new ApiError(
        "Get consent link: existing connection identity, connector parent, or environment could not be verified; no consent request was sent",
        502,
      );
    }
    // Microsoft's source-backed endpoint, deliberately limited to consent:
    // https://github.com/microsoft/power-platform-skills/blob/5a325ffa030cdd0576586bcc1007b996d5d54d52/plugins/power-automate/server/mcp.mjs#L49373
    // ppapiConnectionConsentUrl + authenticateConnection. Other lifecycle
    // operations retain their existing global-host routes.
    const query = new URLSearchParams({
      "api-version": CONNECTIVITY_API_VERSION,
      $filter: `environment eq '${envId}'`,
    });
    const consent = await this.requestConsentJson(
      `https://${host}/connectivity/connectors/${safeConnector}/connections/${safeName}/getConsentLink?${query.toString()}`,
      "POST",
      headers,
      context,
    );
    // This is a handoff, never firstPartyLoginUri token exchange or a grant.
    // Return only the validated URL, with its OAuth query bytes unchanged.
    return { consentLink: validatedConsentLink(consent.consentLink) };
  }

  /** Delete a connection permanently. */
  async deleteConnection(
    connector: string,
    connectionName: string,
    environmentId?: string,
  ): Promise<void> {
    const envId = environmentId ?? this.defaultEnvironmentId;
    const safeConnector = validateConnectorName(connector);
    const safeName = validateConnectionName(connectionName);
    const context = "Delete connection";
    const headers = await this.getAuthHeaders();
    await wrapApiCall(async () => {
      const response = await requestWithRetry(
        this.buildUrl(envId, `/connectors/${safeConnector}/connections/${safeName}`),
        { method: "DELETE", headers },
      );
      return this.handleResponse<Record<string, unknown>>(response, context);
    }, context);
  }
}
